import React, { forwardRef, useImperativeHandle, useRef } from "react";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import useSiteConfig from "@/hooks/useSiteConfig";

/**
 * hCaptcha widget. Hides itself if no site key configured (dev/no-captcha mode).
 * Exposes `getToken()` via ref — caller awaits this in form submit handler.
 * Renders inline with light theme by default; pass `theme="dark"` for dark backgrounds.
 */
const HCaptchaWidget = forwardRef(function HCaptchaWidget({ onVerify, onExpire, theme = "light" }, ref) {
  const { hcaptcha_site_key } = useSiteConfig();
  const captchaRef = useRef(null);
  const tokenRef = useRef("");

  useImperativeHandle(ref, () => ({
    getToken: () => tokenRef.current || "",
    reset: () => {
      tokenRef.current = "";
      try { captchaRef.current?.resetCaptcha?.(); } catch { /* noop */ }
    },
    isConfigured: () => !!hcaptcha_site_key,
  }), [hcaptcha_site_key]);

  if (!hcaptcha_site_key) return null;

  return (
    <div data-testid="hcaptcha-widget" className="mt-1">
      <HCaptcha
        ref={captchaRef}
        sitekey={hcaptcha_site_key}
        theme={theme}
        size="normal"
        onVerify={(t) => { tokenRef.current = t; onVerify?.(t); }}
        onExpire={() => { tokenRef.current = ""; onExpire?.(); }}
        onError={() => { tokenRef.current = ""; }}
      />
    </div>
  );
});

export default HCaptchaWidget;
